/**
 * Checkout tiga langkah.
 *
 *   1. Informasi pembeli — nama, email, WhatsApp
 *   2. Metode pembayaran — diambil langsung dari akun Fansku, fee per metode
 *   3. Bayar — QR atau nomor VA, plus pengecekan status berkala
 *
 * Fansku tidak punya halaman pembayaran sendiri, jadi langkah 3 memang harus
 * kita render: responsnya berisi string QR / nomor VA mentah.
 *
 * Status TIDAK ditentukan di sini. Webhook `donation.paid` yang menerbitkan
 * lisensi; halaman ini hanya menanyakan status pesanan tiap 5 detik supaya
 * pembeli tahu kapan selesai.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { api, type PaymentChannel, type PaymentMethodOption } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { CartLines } from "@/components/CartLines";
import { Field, Input } from "@/components/ui/Form";
import { MonoText } from "@/components/ui/CopyButton";
import { LoadingState } from "@/components/ui/States";
import { formatIDR } from "@/lib/format";
import type { CreateOrderResponse } from "@ag5r-store/shared";

/** Fansku menambahkan fee DI ATAS harga — dihitung untuk ditampilkan. */
function feeFor(m: PaymentMethodOption, amount: number): number {
  return (
    Math.round((amount * (Number(m.fee_percentage) || 0)) / 100) +
    (Number(m.fee_fixed) || 0)
  );
}

const STEPS = [
  { n: 1 as const, label: "Informasi Pembeli" },
  { n: 2 as const, label: "Metode Pembayaran" },
  { n: 3 as const, label: "Bayar" },
];

export function CheckoutPage() {
  const { user, loading: authLoading, signIn } = useAuth();
  const { lines, subtotal, clear } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [channels, setChannels] = useState<PaymentChannel[] | null>(null);
  const [method, setMethod] = useState<PaymentMethodOption | null>(null);
  /** Slug channel yang sedang terbuka; hanya satu agar daftar tetap ringkas. */
  const [openChannel, setOpenChannel] = useState<string | null>(null);

  const [payment, setPayment] = useState<CreateOrderResponse | null>(null);
  const [paid, setPaid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user, email]);

  // Metode dimuat saat masuk langkah 2 — tidak perlu dibebankan ke langkah 1.
  useEffect(() => {
    if (step !== 2 || channels) return;
    api
      .getPaymentMethods()
      .then((list) => {
        setChannels(list);

        // QRIS dipilih otomatis: biayanya paling murah (0,6%) dan bisa dibayar
        // dari hampir semua aplikasi. Channel-nya sekalian dibuka supaya
        // pilihannya terlihat, bukan tersembunyi di grup yang tertutup.
        const qrisChannel = list.find((ch) =>
          ch.payment_methods.some((m) => m.slug === "qris"),
        );
        const qris = qrisChannel?.payment_methods.find((m) => m.slug === "qris");

        setMethod((prev) => prev ?? qris ?? null);
        setOpenChannel((prev) => prev ?? qrisChannel?.slug ?? list[0]?.slug ?? null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Gagal memuat metode"));
  }, [step, channels]);

  const phoneDigits = phone.replace(/[^\d]/g, "");
  const contactValid =
    name.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) &&
    phoneDigits.length >= 8 &&
    phoneDigits.length <= 15;

  const fee = method ? feeFor(method, subtotal) : 0;
  const total = subtotal + fee;

  async function handlePay() {
    if (!method) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.createOrder({
        items: lines.map((l) => ({ variant_id: l.variant_id, quantity: l.quantity })),
        contact: { name: name.trim(), email: email.trim(), phone: phone.trim() },
        payment_method: method.slug,
      });
      setPayment(res);
      // Keranjang dikosongkan setelah pesanan tercatat di server; instruksi
      // bayar tetap tampil karena sudah disimpan di state.
      clear();
      setStep(3);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal membuat pesanan");
    } finally {
      setSubmitting(false);
    }
  }

  // Polling status. Webhook yang bekerja; ini sekadar memberi tahu pembeli.
  const orderId = payment?.order_id;
  useEffect(() => {
    if (!orderId || paid) return;
    let stopped = false;

    const tick = async () => {
      try {
        const s = await api.getOrderStatus(orderId);
        if (stopped) return;
        if (s.status === "PAID") {
          setPaid(true);
        } else if (s.status === "FAILED" || s.status === "EXPIRED") {
          setError(`Pesanan ${s.status === "EXPIRED" ? "kedaluwarsa" : "dibatalkan"}.`);
          stopped = true;
        }
      } catch {
        // Jaringan putus sesaat bukan alasan menghentikan polling.
      }
    };

    const id = window.setInterval(tick, 5000);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [orderId, paid]);

  if (lines.length === 0 && !payment) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading text-2xl font-bold text-ink-primary">
          Keranjang Anda kosong
        </h1>
        <p className="mt-2 font-sans text-sm text-ink-muted">
          Pilih produk dulu, lalu kembali ke sini untuk menyelesaikan pembelian.
        </p>
        <Link to="/products" className="inline-block mt-6">
          <Button variant="primary">Lihat Katalog</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-ink-primary">
        Checkout
      </h1>

      <ol className="mt-6 flex flex-wrap items-center gap-3">
        {STEPS.map((s, i) => (
          <li key={s.n} className="flex items-center gap-3">
            <span
              className={`flex items-center justify-center w-7 h-7 rounded-sm font-mono text-xs ${
                step >= s.n ? "bg-accent text-on-accent" : "border border-border text-ink-dim"
              }`}
            >
              {s.n}
            </span>
            <span
              className={`font-sans text-sm ${step >= s.n ? "text-ink-primary" : "text-ink-dim"}`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="w-6 h-px bg-border" />}
          </li>
        ))}
      </ol>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {step === 1 && (
            <section className="border border-border bg-surface rounded-lg p-6">
              <h2 className="font-heading text-lg font-bold text-ink-primary">
                Informasi Pembeli
              </h2>
              <p className="mt-1 font-sans text-sm text-ink-muted">
                Kami menghubungi Anda lewat WhatsApp bila ada kendala dengan pesanan.
              </p>

              <div className="mt-5 space-y-4">
                <Field label="NAMA LENGKAP" required>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Nama Anda" autoComplete="name" />
                </Field>
                <Field label="EMAIL" required hint="Terisi otomatis dari akun Anda.">
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@email.com" autoComplete="email" />
                </Field>
                <Field label="NOMOR WHATSAPP" required hint="Contoh: 081234567890">
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder="081234567890" autoComplete="tel" />
                </Field>
              </div>

              <Button variant="primary" className="mt-6" disabled={!contactValid}
                onClick={() => setStep(2)}>
                Lanjut ke Pembayaran →
              </Button>
            </section>
          )}

          {step === 2 && (
            <section className="border border-border bg-surface rounded-lg p-6">
              <h2 className="font-heading text-lg font-bold text-ink-primary">
                Pilih Metode Pembayaran
              </h2>
              <p className="mt-1 font-sans text-sm text-ink-muted">
                Biaya layanan berbeda tiap metode dan ditampilkan apa adanya.
              </p>

              {!channels ? (
                <div className="mt-6"><LoadingState label="MEMUAT_METODE" /></div>
              ) : (
                // Accordion: hanya satu channel terbuka. Menampilkan 15+ metode
                // sekaligus membuat halaman panjang dan sulit dibandingkan.
                <div className="mt-5 border border-border rounded-lg divide-y divide-border overflow-hidden">
                  {channels.map((ch) => {
                    const open = openChannel === ch.slug;
                    const chosenHere = ch.payment_methods.find((m) => m.slug === method?.slug);
                    return (
                      <div key={ch.id}>
                        <button
                          type="button"
                          aria-expanded={open}
                          onClick={() => setOpenChannel(open ? null : ch.slug)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left
                                     hover:bg-accent/5 transition-colors"
                        >
                          <span className="flex-1 font-sans text-sm font-medium text-ink-primary">
                            {ch.name}
                          </span>
                          {/* Pilihan tetap terlihat walau grupnya ditutup. */}
                          {chosenHere ? (
                            <span className="font-sans text-xs text-accent">
                              {chosenHere.name}
                            </span>
                          ) : (
                            <span className="font-sans text-xs text-ink-dim">
                              {ch.payment_methods.length} metode
                            </span>
                          )}
                          <span
                            aria-hidden
                            className={`text-ink-muted transition-transform ${open ? "rotate-180" : ""}`}
                          >
                            ▾
                          </span>
                        </button>

                        {open && (
                          <div role="radiogroup" aria-label={ch.name} className="px-3 pb-3 space-y-1.5">
                            {ch.payment_methods.map((m) => {
                              const f = feeFor(m, subtotal);
                              const active = method?.slug === m.slug;
                              return (
                                <button
                                  key={m.id}
                                  type="button"
                                  role="radio"
                                  aria-checked={active}
                                  onClick={() => setMethod(m)}
                                  className={`w-full flex items-center gap-3 rounded-sm border px-3 py-2.5
                                              text-left transition-colors ${
                                                active
                                                  ? "border-accent bg-accent/10"
                                                  : "border-border hover:border-border-strong"
                                              }`}
                                >
                                  {/* Penanda radio — status terpilih harus terbaca
                                      tanpa mengandalkan warna saja. */}
                                  <span
                                    aria-hidden
                                    className={`w-4 h-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                                      active ? "border-accent" : "border-border-strong"
                                    }`}
                                  >
                                    {active && (
                                      <span className="w-2 h-2 rounded-full bg-accent" />
                                    )}
                                  </span>
                                  <span className="flex-1 font-sans text-sm text-ink-primary">
                                    {m.name}
                                  </span>
                                  <span
                                    className={`font-mono text-xs ${
                                      f === 0 ? "text-status-active" : "text-ink-muted"
                                    }`}
                                  >
                                    {f === 0 ? "gratis" : `+ ${formatIDR(f)}`}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {method && (
                <p className="mt-4 font-sans text-sm text-ink-muted">
                  Terpilih: <span className="text-ink-primary">{method.name}</span> ·
                  biaya layanan{" "}
                  <span className="font-mono text-accent">{formatIDR(fee)}</span>
                </p>
              )}

              {error && (
                <div className="mt-5 border border-status-failed rounded-sm px-4 py-3">
                  <MonoText size="xs" color="status-failed">! {error}</MonoText>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <Button variant="ghost" onClick={() => setStep(1)}>← Kembali</Button>
                {!user && !authLoading ? (
                  <Button variant="primary" onClick={() => void signIn().catch(() => {})}>
                    Masuk dengan Google
                  </Button>
                ) : (
                  <Button variant="primary" disabled={!method} loading={submitting}
                    onClick={handlePay}>
                    Bayar {formatIDR(total)}
                  </Button>
                )}
              </div>
            </section>
          )}

          {step === 3 && payment && (
            <PaymentStep payment={payment} paid={paid} error={error}
              onDone={() => navigate("/dashboard")} />
          )}
        </div>

        <aside className="border border-border bg-surface rounded-lg p-5 h-fit">
          <h2 className="font-heading text-base font-bold text-ink-primary">
            Ringkasan Belanja
          </h2>

          <div className="mt-4">
            {payment ? (
              // Pesanan sudah dibuat di server — isi keranjang tidak lagi
              // relevan, nominalnya sudah terkunci di transaksi Fansku.
              <p className="font-sans text-sm text-ink-muted">
                Pesanan sudah dibuat. Nominal terkunci pada transaksi ini.
              </p>
            ) : (
              <>
                <CartLines compact />
                <Link
                  to="/cart"
                  className="inline-block mt-3 font-sans text-xs text-ink-muted underline hover:text-accent"
                >
                  Ubah isi keranjang
                </Link>
              </>
            )}
          </div>

          <dl className="mt-5 pt-4 border-t border-border space-y-2">
            <div className="flex justify-between">
              <dt className="font-sans text-sm text-ink-muted">Subtotal</dt>
              <dd className="font-mono text-sm text-ink-primary">
                {formatIDR(payment?.amount ?? subtotal)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="font-sans text-sm text-ink-muted">Biaya layanan</dt>
              <dd className="font-mono text-sm text-ink-primary">
                {formatIDR(payment?.fee ?? fee)}
              </dd>
            </div>
            <div className="flex justify-between pt-2 border-t border-border">
              <dt className="font-sans text-sm text-ink-muted">Total</dt>
              <dd className="font-mono text-xl text-accent">
                {formatIDR(payment?.total_amount ?? total)}
              </dd>
            </div>
          </dl>

          <div className="mt-5 flex items-start gap-2 border border-border bg-panel rounded-sm px-3 py-2.5">
            <span aria-hidden className="text-accent leading-none mt-0.5">🔒</span>
            <div>
              <p className="font-sans text-xs text-ink-primary">256-Bit Encrypted Payment</p>
              <p className="mt-0.5 font-sans text-2xs text-ink-muted leading-relaxed">
                Pembayaran diproses penyedia berlisensi. Kami tidak menyimpan data kartu
                atau rekening Anda.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/** Langkah 3 — tampilkan QR atau nomor VA, lalu tunggu webhook. */
function PaymentStep({
  payment,
  paid,
  error,
  onDone,
}: {
  payment: CreateOrderResponse;
  paid: boolean;
  error: string | null;
  onDone: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);

  const qrAction = payment.actions.find((a) => a.descriptor === "QR_STRING");
  const otherAction = payment.actions.find((a) => a.descriptor !== "QR_STRING");

  useEffect(() => {
    if (!qrAction || !canvasRef.current) return;
    // Digambar lokal di canvas — string QR tidak pernah dikirim ke layanan
    // pihak ketiga mana pun.
    void QRCode.toCanvas(canvasRef.current, qrAction.value, {
      width: 240,
      margin: 1,
      color: { dark: "#000000", light: "#FFFFFF" },
    }).catch(() => {
      /* gagal render — nilai mentahnya tetap ditampilkan di bawah */
    });
  }, [qrAction]);

  const copy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    });
  }, []);

  if (paid) {
    return (
      <section className="border border-status-active bg-surface rounded-lg p-8 text-center">
        <h2 className="font-heading text-2xl font-bold text-ink-primary">
          Pembayaran diterima
        </h2>
        <p className="mt-2 font-sans text-sm text-ink-muted">
          Produk Anda sudah terbit dan tersimpan di halaman Akun Saya.
        </p>
        <Button variant="primary" className="mt-6" onClick={onDone}>
          Buka Akun Saya →
        </Button>
      </section>
    );
  }

  return (
    <section className="border border-border bg-surface rounded-lg p-6">
      <h2 className="font-heading text-lg font-bold text-ink-primary">
        Selesaikan Pembayaran
      </h2>
      <p className="mt-1 font-sans text-sm text-ink-muted">
        Nomor pesanan{" "}
        <span className="font-mono text-ink-primary">{payment.order_id}</span> ·{" "}
        {payment.channel_code}
      </p>

      {qrAction && (
        <div className="mt-6 flex flex-col items-center">
          {/* Latar putih wajib — QR gelap di atas tema gelap tidak terbaca. */}
          <div className="bg-white p-3 rounded-lg">
            <canvas ref={canvasRef} />
          </div>
          <p className="mt-3 font-sans text-sm text-ink-muted text-center max-w-sm">
            Pindai dengan aplikasi apa pun yang mendukung QRIS, lalu bayar{" "}
            <span className="text-accent font-mono">{formatIDR(payment.total_amount)}</span>.
          </p>
        </div>
      )}

      {otherAction && (
        <div className="mt-6">
          <MonoText size="xs" color="ink-muted" className="block mb-1.5">
            {otherAction.descriptor.replace(/_/g, " ")}
          </MonoText>
          <div className="flex items-center gap-2 border border-border bg-panel rounded-sm px-3 py-2.5">
            <code className="flex-1 font-mono text-sm text-accent break-all">
              {otherAction.value}
            </code>
            <Button variant="ghost" onClick={() => copy(otherAction.value)}>
              {copied ? "Tersalin" : "Salin"}
            </Button>
          </div>
          <p className="mt-2 font-sans text-sm text-ink-muted">
            Bayar tepat <span className="text-accent font-mono">{formatIDR(payment.total_amount)}</span>.
          </p>
        </div>
      )}

      {error ? (
        <div className="mt-6 border border-status-failed rounded-sm px-4 py-3">
          <MonoText size="xs" color="status-failed">! {error}</MonoText>
        </div>
      ) : (
        <div className="mt-6 flex items-center gap-2 border border-border bg-panel rounded-sm px-4 py-3">
          <span className="w-2 h-2 rounded-full bg-status-pending animate-pulse-terminal" />
          <p className="font-sans text-sm text-ink-muted">
            Menunggu pembayaran. Halaman ini akan berubah sendiri begitu dana masuk —
            tidak perlu di-refresh.
          </p>
        </div>
      )}

      <p className="mt-4 font-sans text-xs text-ink-dim">
        Pesanan berlaku 2 jam. Lewat dari itu, pesanan dibatalkan otomatis dan
        stok dilepas kembali.
      </p>
    </section>
  );
}
